import { useState } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { testimonials, advantages } from '@/mocks/data';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMsg, setFormMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMsg('提交成功，我们会尽快联系您！');
      form.reset();
      return;
    }
    setFormStatus('loading');
    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9u18ddca23r0tr1aohg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      const code = (parsed as { code?: string }).code;
      if (res.ok && code === 'OK') {
        setFormStatus('success');
        setFormMsg('感谢您的留言！我们会尽快与您联系。');
        form.reset();
      } else {
        setFormStatus('error');
        const serverMsg = (parsed as { meta?: { message?: string } }).meta?.message
          || (parsed as { message?: string }).message
          || responseText;
        setFormMsg(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试');
      }
    } catch {
      setFormStatus('error');
      setFormMsg('网络错误，请稍后再试');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于悠宝三岁',
    description: '悠宝三岁是专为3岁儿童设计的综合成长解决方案，涵盖营养、早教、习惯培养等核心需求。',
    about: {
      '@type': 'Organization',
      name: '悠宝三岁',
      description: '专为3岁儿童设计的综合成长解决方案',
      foundingDate: '2022',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+86-400-123-4567',
        contactType: 'customer service',
        availableLanguage: 'Chinese',
      },
    },
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description="悠宝三岁品牌故事——专为3岁儿童打造的综合成长方案。了解我们的理念、团队和使命，以及超过10万家庭的信赖之选。"
        keywords="悠宝三岁品牌介绍,悠宝三岁团队,悠宝三岁口碑,育儿品牌"
        canonicalPath="/about"
        schema={aboutSchema}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-6xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        <section className="relative py-16 md:py-24 px-4 md:px-6 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Bright%20warm%20modern%20office%20space%20with%20natural%20light%2C%20minimalist%20design%2C%20cream%20walls%2C%20wooden%20furniture%2C%20plants%2C%20children%20education%20brand%20workplace%2C%20cozy%20creative%20atmosphere%2C%20team%20collaboration%20area&width=1800&height=600&seq=about-hero&orientation=landscape"
            alt="悠宝三岁团队"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/40"></div>
          <div className="relative z-10 text-center max-w-2xl mx-auto">
            <h1 className="font-heading text-3xl md:text-5xl text-background-50 mb-4">
              关于悠宝三岁
            </h1>
            <p className="text-background-50/85 text-sm md:text-base leading-relaxed">
              陪伴每个3岁宝宝度过最关键的成长阶段，用科学与爱守护童年
            </p>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-4xl text-foreground-950 mb-4">我们的故事</h2>
            </div>
            <div className="space-y-6 text-foreground-700 text-sm md:text-base leading-relaxed">
              <p>
                <strong className="text-foreground-900">悠宝三岁</strong>诞生于2022年，由一群热爱儿童教育事业的专家和父母共同创立。我们深知3岁是人生中最重要的发育阶段之一——大脑发育完成85%，语言能力爆发，性格与习惯逐渐成形。
              </p>
              <p>
                然而，市面上针对3岁宝宝的综合成长方案却少之又少。要么是面向0-3岁的早教机构，要么是面向4-6岁的学前准备，3岁这个关键的"过渡期"被严重忽视。
              </p>
              <p>
                <strong className="text-foreground-900">悠宝三岁</strong>正是为了填补这个空白而生。我们联合儿科医生、注册营养师、早教专家和儿童心理学家，历时两年研发了一套涵盖<strong>营养、早教、习惯培养</strong>三大维度的科学成长方案。
              </p>
              <p>
                我们的理念很简单：<strong className="text-primary-600">不需要昂贵的早教班，不需要复杂的营养食谱，不需要焦虑和攀比</strong>。每天30分钟，在家就能给宝宝最科学的陪伴。因为我们相信，最好的教育发生在温暖的家庭日常中。
              </p>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading text-2xl md:text-4xl text-foreground-950 mb-3">为什么选择悠宝三岁</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {advantages.map((item, index) => (
                <div key={index} className="bg-background-50 rounded-xl p-6 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-accent-100 flex items-center justify-center">
                    <i className={`${item.icon} text-accent-600 text-2xl`}></i>
                  </div>
                  <h3 className="font-heading text-base text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading text-2xl md:text-4xl text-foreground-950 mb-3">家长口碑</h2>
              <p className="text-foreground-600 text-sm md:text-base">来自真实用户的真诚反馈</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((item) => (
                <div key={item.id} className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={item.avatar}
                      alt={item.name}
                      className="w-10 h-10 rounded-full object-cover"
                      loading="lazy"
                    />
                    <div>
                      <div className="text-sm font-medium text-foreground-900">{item.name}</div>
                      <div className="text-xs text-foreground-500">{item.city}</div>
                    </div>
                  </div>
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: item.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-secondary-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-foreground-700 text-sm leading-relaxed">{item.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-4xl text-foreground-950 mb-3">联系我们</h2>
              <p className="text-foreground-600 text-sm md:text-base">
                有任何问题或建议？欢迎随时联系我们
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
              <div className="lg:col-span-2 space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-primary-600 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-foreground-900 mb-1">客服热线</h4>
                    <a href="tel:400-123-4567" className="text-sm text-foreground-600 hover:text-primary-500 transition-colors">
                      400-123-4567
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-accent-600 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-foreground-900 mb-1">电子邮箱</h4>
                    <a href="mailto:hello@ohpwbf.cn" className="text-sm text-foreground-600 hover:text-primary-500 transition-colors">
                      hello@ohpwbf.cn
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-secondary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-secondary-600 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-foreground-900 mb-1">公司地址</h4>
                    <p className="text-sm text-foreground-600">上海市浦东新区张江高科技园区碧波路888号</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-primary-600 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-foreground-900 mb-1">工作时间</h4>
                    <p className="text-sm text-foreground-600">周一至周五 9:00 - 18:00</p>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-3">
                <form data-readdy-form="true" onSubmit={handleSubmit} className="bg-background-50 rounded-xl border border-background-200/70 p-6 space-y-4">
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" readOnly aria-hidden="true" className="honeypot-field" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground-800 mb-1.5">姓名 *</label>
                      <input
                        type="text"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg outline-none focus:border-primary-400 transition-colors"
                        placeholder="您的姓名"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-800 mb-1.5">手机号 *</label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg outline-none focus:border-primary-400 transition-colors"
                        placeholder="您的手机号"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-800 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      name="email"
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg outline-none focus:border-primary-400 transition-colors"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-800 mb-1.5">留言内容 *</label>
                    <textarea
                      name="message"
                      rows={4}
                      required
                      maxLength={500}
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请描述您的问题或需求（限500字）"
                    ></textarea>
                  </div>
                  {formStatus !== 'idle' && (
                    <p className={`text-sm ${formStatus === 'success' ? 'text-accent-600' : 'text-primary-600'}`}>
                      {formMsg}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
                  >
                    {formStatus === 'loading' ? '提交中...' : '发送留言'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}